<!--
  Модалка «Выдать в пользование». По ТЗ 10.09.2026:
  выдача НЕ уменьшает остаток на складе, только переводит счёт «свободно»
  → «у пользователя X» до фактического возврата или списания.
-->
<template>
  <div class="ifum__backdrop" @mousedown.self="$emit('close')">
    <div class="ifum" role="dialog" @mousedown.stop>
      <header class="ifum__head">
        <h3>{{ $t('household.service.addIssue') }}</h3>
        <button class="ifum__close" @click="$emit('close')" aria-label="×">×</button>
      </header>

      <div class="ifum__body">
        <div v-if="error" class="ifum__err">{{ error }}</div>

        <label class="ifum__field">
          <span>{{ $t('household.service.formHolder') }}</span>
          <select v-model="form.holder">
            <option :value="null">— {{ $t('common.select') }} —</option>
            <option v-for="u in holders" :key="u.id" :value="u.id">
              №{{ u.personal_number || '?' }} {{ u.fio || u.full_name }}
            </option>
          </select>
        </label>

        <label class="ifum__field">
          <span>{{ $t('household.service.formNomenclature') }}</span>
          <select v-model="form.nomenclature">
            <option :value="null">— {{ $t('common.select') }} —</option>
            <option v-for="n in nomenclature" :key="n.id" :value="n.id">
              {{ n.name }} ({{ n.unit }})
            </option>
          </select>
        </label>

        <label class="ifum__field">
          <span>{{ $t('household.service.formQuantity') }}</span>
          <input type="number" min="0" step="0.01" v-model.number="form.quantity" />
        </label>

        <label class="ifum__field">
          <span>{{ $t('household.service.formIssuedAt') }}</span>
          <input type="date" v-model="form.issued_at" />
        </label>

        <label class="ifum__field">
          <span>{{ $t('household.service.formDueDate') }}</span>
          <input type="date" v-model="form.due_date" />
        </label>

        <label class="ifum__field">
          <span>{{ $t('household.service.formOrderRef') }}</span>
          <input type="text" v-model="form.order_ref" :placeholder="$t('household.service.formOrderRefPh')" />
        </label>

        <label class="ifum__field ifum__field--wide">
          <span>{{ $t('household.service.formComment') }}</span>
          <input type="text" v-model="form.comment" />
        </label>
      </div>

      <footer class="ifum__foot">
        <button class="btn btn--secondary" @click="$emit('close')">{{ $t('common.cancel') }}</button>
        <button class="btn btn--primary" :disabled="saving" @click="save">
          {{ saving ? $t('common.saving') : $t('household.service.doIssue') }}
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { createIssue, listNomenclature } from '@/api/wmsService'
import { listBookUsers } from '@/api/bookUsers'

export default {
  name: 'IssueForUseModal',
  props: {
    service: { type: Object, default: null },
    // По ТЗ 10.09.2026: склад известен из контекста страницы (переключается
    // в шапке WarehouseServicePage), в модалке его не спрашиваем.
    warehouseId: { type: [Number, String], default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      saving: false, error: null,
      nomenclature: [], holders: [],
      form: {
        holder: null, nomenclature: null,
        quantity: 1,
        issued_at: this.todayISO(),
        due_date: '',
        order_ref: '', comment: '',
      },
    }
  },
  async mounted() {
    try {
      this.nomenclature = await listNomenclature()
      const users = await listBookUsers({ is_archived: false })
      this.holders = users.map(u => ({
        id: u.user?.id || u.user_id,
        personal_number: u.personal_number,
        fio: u.fio || u.full_name || u.user?.full_name,
      })).filter(u => u.id)
    } catch (e) { console.error(e) }
  },
  methods: {
    todayISO() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` },
    async save() {
      if (!this.warehouseId || !this.form.holder || !this.form.nomenclature || !this.form.quantity) {
        this.error = this.$t('common.fillRequired'); return
      }
      this.saving = true; this.error = null
      try {
        const payload = {
          warehouse: this.warehouseId,
          holder: this.form.holder,
          nomenclature: this.form.nomenclature,
          quantity: this.form.quantity,
          issued_at: this.form.issued_at,
          due_date: this.form.due_date || null,
          order_ref: this.form.order_ref || '',
          comment: this.form.comment || '',
        }
        await createIssue(payload)
        this.$emit('saved')
      } catch (e) {
        this.error = e?.response?.data ? JSON.stringify(e.response.data) : (e.message || 'Save failed')
      } finally { this.saving = false }
    },
  },
}
</script>

<style scoped>
.ifum__backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.ifum { background: #fff; border-radius: 12px; width: min(600px, 96vw); max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 20px 60px rgba(0,0,0,.25); overflow: hidden; }
.ifum__head { display: flex; justify-content: space-between; align-items: center; padding: 14px 20px; border-bottom: 1px solid #E5E7EB; }
.ifum__head h3 { margin: 0; font-size: 17px; }
.ifum__close { background: none; border: 0; font-size: 24px; cursor: pointer; color: #6B7280; }
.ifum__body { padding: 16px 20px; overflow: auto; display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
.ifum__field { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151; }
.ifum__field--wide { grid-column: 1 / -1; }
.ifum__field > span { font-weight: 600; color: #6B7280; }
.ifum__field input, .ifum__field select {
  padding: 8px 10px; border: 1px solid #D1D5DB; border-radius: 8px; font-family: inherit; font-size: 13px;
}
.ifum__err { grid-column: 1 / -1; color: #B91C1C; font-size: 13px; padding: 8px; background: #FEE2E2; border-radius: 6px; }
.ifum__foot { display: flex; justify-content: flex-end; gap: 10px; padding: 12px 20px; border-top: 1px solid #E5E7EB; }
.btn { padding: 8px 16px; border-radius: 8px; border: 1px solid transparent; cursor: pointer; font-family: inherit; font-size: 13px; font-weight: 600; }
.btn--primary { background: #2F78EE; color: #fff; }
.btn--primary:disabled { opacity: .5; }
.btn--secondary { background: #fff; border-color: #D1D5DB; color: #374151; }
</style>
